import { useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { track } from '@/core/tracking/tracking';

interface RouteWithTrackingProps {
  children: ReactNode;
}

/**
 * Wrapper component that tracks page views
 * Should wrap all routes in the router
 * Tracks PageView event when route changes
 */
export function RouteWithTracking({ children }: RouteWithTrackingProps) {
  const location = useLocation();
  // Track which path was last tracked to prevent duplicates (StrictMode protection)
  const lastTrackedPathRef = useRef<string | null>(null);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}`;
    
    // Skip if we already tracked this exact path (prevents StrictMode double-fire)
    if (lastTrackedPathRef.current === currentPath) {
      return;
    }
    
    lastTrackedPathRef.current = currentPath;
    
    // Track page view when route changes
    // Use standard PageView event for Meta/GA4
    track('PageView', {
      page_path: location.pathname,
      page_title: document.title || location.pathname,
      page_location: window.location.href,
      search: location.search,
    });
  }, [location.pathname, location.search]);

  return <>{children}</>;
}
